import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/axios';

export type SystemMessageColor = 'RED' | 'BLUE';

export interface SystemMessageData {
    id: string | null;
    messageText: string;
    color: SystemMessageColor;
    animated: boolean;
    enabled: boolean;
}

interface SystemMessageState {
    data: SystemMessageData | null;
    dismissedText: string | null;
    loading: boolean;
}

const LOCAL_STORAGE_DISMISSED_KEY = 'moonskins_dismissed_system_message';

const initialDismissed = typeof window !== 'undefined'
    ? localStorage.getItem(LOCAL_STORAGE_DISMISSED_KEY)
    : null;

const initialState: SystemMessageState = {
    data: null,
    dismissedText: initialDismissed,
    loading: false,
};

// Fetch initial system message state for clients
export const fetchSystemMessage = createAsyncThunk(
    'systemMessage/fetchSystemMessage',
    async () => {
        const response = await api.get<SystemMessageData>('/system/message');
        return response.data;
    }
);

const systemMessageSlice = createSlice({
    name: 'systemMessage',
    initialState,
    reducers: {
        dismissCurrentMessage: (state) => {
            if (state.data?.messageText) {
                state.dismissedText = state.data.messageText as string;
                localStorage.setItem(LOCAL_STORAGE_DISMISSED_KEY, state.data.messageText as string);
            }
        },
        setSystemMessageData: (state, action: PayloadAction<SystemMessageData>) => {
            state.data = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSystemMessage.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSystemMessage.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchSystemMessage.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { dismissCurrentMessage, setSystemMessageData } = systemMessageSlice.actions;
export default systemMessageSlice.reducer;